/**
 * The organisation's data protection check, as the sender presses Send
 * (PRD Phase 6.5.4, ADR-0022). The API answers what would happen to the
 * draft if it went now; the composer asks before sending only when that
 * changes something for the sender. The mail store checks again as the
 * message is sent, so a check that fails never stops a send.
 */

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { DlpCheck, DlpDraft } from 'zitimail-shared-types';

import { api } from '../admin/api';
import { findingInWords } from '../admin/dlp';

/** A check that does not answer in this long is treated as failed. */
const CHECK_TIMEOUT_MS = 8000;

/** The answers that need the sender to decide; anything else sends as normal. */
export type DlpStop = Exclude<DlpCheck, { action: 'none' }> & {
  action: 'warn' | 'encrypt' | 'quarantine' | 'block';
};

export function needsDecision(check: DlpCheck): check is DlpStop {
  return ['warn', 'encrypt', 'quarantine', 'block'].includes(check.action);
}

/** Asks the API; rejects on any failure, including taking too long. */
export async function checkDraft(tenant: string, draft: DlpDraft): Promise<DlpCheck> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), CHECK_TIMEOUT_MS);
  try {
    return await api<DlpCheck>(`/tenants/${encodeURIComponent(tenant)}/dlp/check`, {
      method: 'POST',
      body: draft,
      signal: controller.signal,
    });
  } finally {
    window.clearTimeout(timer);
  }
}

interface Words {
  readonly title: string;
  readonly lead: string;
  /** Null: the sender cannot go ahead. */
  readonly go: string | null;
  readonly back: string;
}

function wordsFor(check: DlpStop): Words {
  switch (check.action) {
    case 'warn':
      return {
        title: 'This message may contain sensitive information',
        lead: "Your organisation's data protection policy found something it asks you to check before sending.",
        go: 'Send anyway',
        back: 'Go back and edit',
      };
    case 'encrypt':
      return check.mixed_recipients
        ? {
            title: 'This message will only be delivered over an encrypted connection',
            lead: 'It goes to colleagues and to people outside the organisation together, so it will be refused. Send it to your colleagues and to external recipients as two separate messages.',
            go: null,
            back: 'Cancel',
          }
        : {
            title: 'This message will only be delivered over an encrypted connection',
            lead: "If a recipient's mail server cannot accept an encrypted connection, the message comes back to you undelivered.",
            go: 'Send',
            back: 'Cancel',
          };
    case 'quarantine':
      return {
        title: 'This message will be held for review by your administrator before it is delivered',
        lead: 'An administrator will release it to its recipients or delete it.',
        go: 'Send for review',
        back: 'Cancel',
      };
    case 'block':
      return {
        title: 'This message cannot be sent',
        lead: "Your organisation's data protection policy does not allow it.",
        go: null,
        back: 'Close',
      };
  }
}

/**
 * What the check found, and the sender's choice. Focus starts on the safe
 * button for a warning, and on the only way forward otherwise.
 */
export function DlpDialog({
  check,
  onSend,
  onBack,
}: {
  check: DlpStop;
  onSend: () => void;
  onBack: () => void;
}): JSX.Element {
  const words = wordsFor(check);
  const focusGo = words.go !== null && check.action !== 'warn';

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onBack();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onBack]);

  return createPortal(
    <div className="modal-scrim" role="presentation">
      <div
        className="dlp-dialog"
        role={check.action === 'warn' || check.action === 'block' ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-labelledby="dlp-dialog-title"
        aria-describedby="dlp-dialog-lead"
        // Keys pressed here stay here: the composer's Cmd+Enter must not send.
        onKeyDown={(e) => e.stopPropagation()}
      >
        <h3 id="dlp-dialog-title">{words.title}</h3>
        <p id="dlp-dialog-lead">{words.lead}</p>
        {check.message && <p className="dlp-dialog-why">{check.message}</p>}
        {(check.findings.length > 0 || check.attachments.length > 0) && (
          <ul className="dlp-dialog-found">
            {check.findings.map((f) => (
              <li key={f.kind}>{findingInWords(f)}</li>
            ))}
            {check.attachments.map((name) => (
              <li key={`file:${name}`}>Attachment not allowed: {name}</li>
            ))}
          </ul>
        )}
        <div className="dlp-dialog-acts">
          <button
            type="button"
            className={words.go === null || check.action === 'warn' ? 'btn' : 'btn ghost'}
            onClick={onBack}
            autoFocus={!focusGo}
          >
            {words.back}
          </button>
          {words.go !== null && (
            <button
              type="button"
              className={check.action === 'warn' ? 'btn ghost' : 'btn'}
              onClick={onSend}
              autoFocus={focusGo}
            >
              {words.go}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
